import React, { useState, useEffect } from 'react';
import { jobService } from '../../services/api';
import { Plus, Search, Edit2, Trash2, X, Briefcase, MapPin, Clock, IndianRupee, Users, FileText, CheckCircle, AlertCircle, Inbox, ChevronDown, ChevronRight } from 'lucide-react';

const APP_TYPE_LABELS = {
  employment: 'Employment',
  business_coordinator: 'Business Coordinator',
  business_associates: 'Business Associates',
  internship: 'Internship Programme',
};

const APP_TYPE_COLORS = {
  employment: 'bg-red-500/10 text-red-500',
  business_coordinator: 'bg-blue-500/10 text-blue-500',
  business_associates: 'bg-green-500/10 text-green-500',
  internship: 'bg-purple-500/10 text-purple-500',
};

function JobsManagement() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedJobApplications, setSelectedJobApplications] = useState([]);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('jobs'); // 'jobs' | 'applications'
  const [appTypeFilter, setAppTypeFilter] = useState('all');
  const [allApplications, setAllApplications] = useState([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [expandedApp, setExpandedApp] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    location: '',
    type: 'full_time',
    department: 'employment',
    salary_min: '',
    salary_max: '',
    status: 'active',
  });

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    if (activeTab === 'applications') {
      loadAllApplications();
    }
  }, [activeTab, appTypeFilter]);

  const loadAllApplications = async () => {
    setApplicationsLoading(true);
    try {
      const data = await jobService.allApplications({ application_type: appTypeFilter });
      setAllApplications(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await jobService.index();
      setJobs(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingJob) {
        await jobService.update(editingJob.id, formData);
      } else {
        await jobService.store(formData);
      }
      closeModal();
      loadJobs();
    } catch (error) {
      alert('Error saving job: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || '',
      description: job.description || '',
      requirements: job.requirements || '',
      location: job.location || '',
      type: job.type || 'full_time',
      department: job.department || 'employment',
      salary_min: job.salary_min || '',
      salary_max: job.salary_max || '',
      status: job.status || 'active',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to archive this job posting?')) {
      try {
        await jobService.destroy(id);
        loadJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
      }
    }
  };

  const handleViewApplications = async (jobId) => {
    try {
      const data = await jobService.applications(jobId);
      setSelectedJobApplications(Array.isArray(data) ? data : data?.data || []);
      setShowApplicationsModal(true);
    } catch (error) {
      alert('Error fetching applications: ' + (error.response?.data?.message || error.message));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
    setFormData({ title: '', description: '', requirements: '', location: '', type: 'full_time', department: 'employment', salary_min: '', salary_max: '', status: 'active' });
  };

  const filteredJobs = jobs.filter(j => 
    j.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    j.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case 'active': return 'bg-red-500/10 text-red-500';
      case 'closed': return 'bg-red-500/10 text-red-500';
      default: return 'bg-slate-500/10 text-slate-500';
    }
  };

  const renderExtraData = (extraData) => {
    if (!extraData || typeof extraData !== 'object') return null;
    const entries = Object.entries(extraData).filter(([k]) => k !== 'application_type');
    if (entries.length === 0) return null;
    return (
      <div className="mt-3 space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="flex gap-2 text-xs">
            <span className="text-slate-500 font-semibold capitalize min-w-[140px]">{key.replace(/_/g, ' ')}:</span>
            <span className="text-slate-300 break-all">
              {typeof value === 'string' && value.startsWith('http') ? (
                <a href={value} target="_blank" rel="noreferrer" className="text-red-400 hover:underline">View File</a>
              ) : String(value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="text-slate-400 animate-pulse">Syncing talent portal...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">Career Management</h2>
          <p className="text-sm text-slate-500 mt-1">Post opportunities and manage incoming talent applications.</p>
        </div>
        {activeTab === 'jobs' && (
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center justify-center gap-2 bg-red-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
          >
            <Plus size={18} />
            Post New Job
          </button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-800">
        <button
          onClick={() => setActiveTab('jobs')}
          className={`px-5 py-2.5 font-bold text-sm transition-all ${activeTab === 'jobs' ? 'text-red-500 border-b-2 border-red-500' : 'text-slate-500 hover:text-slate-300'}`}
        >
          <Briefcase size={14} className="inline mr-2" />
          Job Postings
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-5 py-2.5 font-bold text-sm transition-all ${activeTab === 'applications' ? 'text-red-500 border-b-2 border-red-500' : 'text-slate-500 hover:text-slate-300'}`}
        >
          <Inbox size={14} className="inline mr-2" />
          Applications
        </button>
      </div>

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="space-y-4">
          {/* Application Type Filter */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setAppTypeFilter('all')}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${appTypeFilter === 'all' ? 'bg-slate-700 text-white' : 'bg-slate-900 text-slate-500 hover:bg-slate-800'}`}
            >
              All
            </button>
            {Object.entries(APP_TYPE_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setAppTypeFilter(key)}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${appTypeFilter === key ? APP_TYPE_COLORS[key] + ' border border-current' : 'bg-slate-900 text-slate-500 hover:bg-slate-800'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Applications List */}
          {applicationsLoading ? (
            <div className="text-slate-400 animate-pulse py-8 text-center">Loading applications...</div>
          ) : allApplications.length > 0 ? (
            <div className="space-y-3">
              {allApplications.map((app) => {
                const appType = app.extra_data?.application_type || 'employment';
                const isExpanded = expandedApp === app.id;
                return (
                  <div key={app.id} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                    <div
                      className="p-4 cursor-pointer hover:bg-slate-800/30 transition-colors"
                      onClick={() => setExpandedApp(isExpanded ? null : app.id)}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {isExpanded ? <ChevronDown size={16} className="text-slate-500 shrink-0" /> : <ChevronRight size={16} className="text-slate-500 shrink-0" />}
                          <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-red-500 border border-slate-700 shrink-0">
                            <Users size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="font-bold text-slate-200 truncate">{app.first_name} {app.last_name}</div>
                            <div className="text-xs text-slate-500 truncate">{app.email} {app.phone && `• ${app.phone}`}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${APP_TYPE_COLORS[appType] || 'bg-slate-500/10 text-slate-500'}`}>
                            {APP_TYPE_LABELS[appType] || appType}
                          </span>
                          <span className="text-[10px] text-slate-600">{new Date(app.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-4 border-t border-slate-800 pt-4">
                        {/* Resume & Files */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {app.resume_url && (
                            <a href={app.resume_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Resume
                            </a>
                          )}
                          {app.salary_slip_url && (
                            <a href={app.salary_slip_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Salary Slip
                            </a>
                          )}
                          {app.photo_url && (
                            <a href={app.photo_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Photo
                            </a>
                          )}
                          {app.extra_data?.aadhar_card_url && (
                            <a href={app.extra_data.aadhar_card_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Aadhar
                            </a>
                          )}
                          {app.extra_data?.last_passing_result_url && (
                            <a href={app.extra_data.last_passing_result_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Passing Result
                            </a>
                          )}
                          {app.extra_data?.student_id_url && (
                            <a href={app.extra_data.student_id_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> Student ID
                            </a>
                          )}
                          {app.extra_data?.pan_card_url && (
                            <a href={app.extra_data.pan_card_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-xs hover:bg-slate-700">
                              <FileText size={12} /> PAN
                            </a>
                          )}
                        </div>

                        {/* Cover Letter */}
                        {app.cover_letter && (
                          <div className="bg-slate-950/50 rounded-xl p-3 border border-slate-800 mb-3">
                            <div className="text-[10px] uppercase font-black text-slate-600 mb-1">Cover Letter</div>
                            <p className="text-xs text-slate-400 italic">"{app.cover_letter}"</p>
                          </div>
                        )}

                        {/* Extra Data Fields */}
                        {renderExtraData(app.extra_data)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                <Inbox size={32} />
              </div>
              <p className="text-slate-500 font-medium">No applications found for this filter.</p>
            </div>
          )}
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === 'jobs' && (
        <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden">
        <div className="p-4 border-b border-slate-800 bg-slate-900/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
            <input
              type="text"
              placeholder="Search by job title or location..."
              className="w-full bg-slate-950 border border-slate-800 rounded-xl py-2.5 pl-10 pr-4 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-950/50">
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Job Title</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Location & Type</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Salary Range</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500">Status</th>
                <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-slate-500 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-slate-800/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-slate-800 flex items-center justify-center text-red-500 border border-slate-700">
                        <Briefcase size={18} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-200">{job.title}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-slate-500 uppercase tracking-widest font-black">
                            Posted: {new Date(job.created_at).toLocaleDateString()}
                          </span>
                          {job.department && (
                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${APP_TYPE_COLORS[job.department] || 'bg-slate-500/10 text-slate-500'}`}>
                              {APP_TYPE_LABELS[job.department] || job.department}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1 text-sm">
                      <div className="text-slate-300 flex items-center gap-2">
                        <MapPin size={12} className="text-red-500" />
                        {job.location}
                      </div>
                      <div className="text-slate-500 flex items-center gap-2">
                        <Clock size={12} />
                        {job.type?.replace('_', ' ')}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-slate-100 font-mono text-xs">
                      <IndianRupee size={12} className="text-red-500" />
                      {job.salary_min ? job.salary_min.toLocaleString() : 'N/A'} - {job.salary_max ? job.salary_max.toLocaleString() : 'N/A'}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${getStatusStyle(job.status)}`}>
                      {job.status === 'active' ? <CheckCircle size={10} /> : <AlertCircle size={10} />}
                      {job.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleViewApplications(job.id)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-slate-300 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-600 hover:text-white transition-all"
                      >
                        <Users size={14} />
                        Applications
                      </button>
                      <button
                        onClick={() => handleEdit(job)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Edit Post"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(job.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                        title="Archive Post"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredJobs.length === 0 && (
            <div className="text-center py-20">
              <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                <Briefcase size={32} />
              </div>
              <p className="text-slate-500 font-medium">No job opportunities posted yet.</p>
            </div>
          )}
        </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <h3 className="text-lg font-bold text-slate-100">
                {editingJob ? 'Refine Job Opportunity' : 'Draft New Opportunity'}
              </h3>
              <button onClick={closeModal} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[80vh]">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Job Title</label>
                <input
                  type="text"
                  required
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Regional Sales Manager"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Location</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g. Remote / Head Office"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Employment Type</label>
                  <select
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors appearance-none"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="full_time">Full Time</option>
                    <option value="part_time">Part Time</option>
                    <option value="contract">Contract</option>
                    <option value="remote">Remote Only</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Career Category</label>
                <select
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors appearance-none"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                >
                  <option value="employment">Employment</option>
                  <option value="business_coordinator">Business Coordinator</option>
                  <option value="business_associates">Business Associates</option>
                  <option value="internship">Internship Programme</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Opportunity Description</label>
                <textarea
                  required
                  rows={4}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors resize-none"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role, impact, and daily responsibilities..."
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Core Requirements</label>
                <textarea
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors resize-none"
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="Key qualifications, experience, and skills needed..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Min Salary (Annual ₹)</label>
                  <input
                    type="number"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.salary_min}
                    onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Max Salary (Annual ₹)</label>
                  <input
                    type="number"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 focus:outline-none focus:border-red-500 transition-colors"
                    value={formData.salary_max}
                    onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-black tracking-widest text-slate-500">Publishing Status</label>
                <div className="flex gap-4">
                  {['draft', 'active', 'closed'].map(s => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFormData({ ...formData, status: s })}
                      className={`flex-1 py-2.5 rounded-xl text-[10px] uppercase font-black tracking-widest border transition-all ${
                        formData.status === s 
                          ? getStatusStyle(s) + ' border-current'
                          : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-2.5 rounded-xl text-sm font-semibold text-slate-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-8 py-2.5 bg-red-600 text-white rounded-xl font-bold hover:bg-red-500 transition-all shadow-lg shadow-red-900/20"
                >
                  {editingJob ? 'Push Updates' : 'Publish Opportunity'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showApplicationsModal && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300">
            <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
              <div>
                <h3 className="text-lg font-bold text-slate-100">Applicant Tracking</h3>
                <p className="text-xs text-slate-500 uppercase tracking-widest font-black mt-0.5">Talent Pool Records</p>
              </div>
              <button onClick={() => setShowApplicationsModal(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-4">
              {selectedJobApplications.length > 0 ? (
                selectedJobApplications.map((app) => (
                  <div key={app.id} className="bg-slate-950 border border-slate-800 rounded-2xl p-5 hover:border-red-500/50 transition-colors">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-xl bg-red-500/10 flex items-center justify-center text-red-500">
                          <UserCircle size={24} />
                        </div>
                        <div>
                          <p className="font-bold text-slate-100">{app.first_name} {app.last_name}</p>
                          <p className="text-sm text-slate-500 font-mono">{app.email}</p>
                          {app.phone && <p className="text-xs text-slate-500 font-mono">{app.phone}</p>}
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-red-500/10 text-red-500 rounded-full text-[10px] font-black uppercase tracking-widest">
                        {app.status}
                      </span>
                    </div>

                    {app.cover_letter && (
                      <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 mb-3">
                        <div className="text-[10px] uppercase font-black text-slate-600 mb-2 flex items-center gap-2">
                          <FileText size={10} />
                          Cover Letter
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed italic">"{app.cover_letter}"</p>
                      </div>
                    )}

                    {app.resume_url && (
                      <div className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-3 border border-slate-800">
                        <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center text-red-500 shrink-0">
                          <FileText size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] uppercase font-black text-slate-600">Resume / CV</div>
                          <div className="text-xs text-slate-400 truncate">{app.resume_url.split('/').pop()}</div>
                        </div>
                        <a
                          href={app.resume_url}
                          target="_blank"
                          rel="noreferrer"
                          className="shrink-0 px-3 py-1.5 bg-red-600 hover:bg-red-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest transition"
                        >
                          View PDF
                        </a>
                      </div>
                    )}

                    <div className="text-[10px] text-slate-600 mt-3">
                      Applied: {new Date(app.created_at).toLocaleString()}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="bg-slate-800 w-16 h-16 rounded-3xl flex items-center justify-center mx-auto mb-4 text-slate-600">
                    <Users size={32} />
                  </div>
                  <p className="text-slate-500 font-medium italic text-sm">No talent records matching this opportunity yet.</p>
                </div>
              )}
            </div>
            
            <div className="p-4 bg-slate-950 border-t border-slate-800 flex justify-end">
              <button
                onClick={() => setShowApplicationsModal(false)}
                className="px-8 py-2.5 bg-slate-800 text-slate-300 rounded-xl text-sm font-bold hover:bg-slate-700 transition-colors"
              >
                Close Pool
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const UserCircle = ({ size }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
);

export default JobsManagement;

